/**
 * Quick add mobile.
 *
 * Un único drawer inferior, compartido por todas las product cards. Los chips
 * de talle se construyen a partir del JSON de variantes que emite la card,
 * filtrando por el color activo. Ese color se lee del [data-variant-link] de la
 * card recién en el momento de abrir, que es lo que hace que el drawer muestre
 * el color correcto sin tocar ColorSwatch: updateLinks() ya mantiene ese href
 * al día cada vez que se toca un swatch.
 */
class QuickAddDrawer extends DrawerComponent {
  constructor() {
    super();

    this.selectors = {
      title: '[data-quick-add-title]',
      price: '[data-quick-add-price]',
      sizes: '[data-quick-add-sizes]',
      form: 'form[is="product-form"]',
      variantInput: '[name=id]',
      cardTitle: '.product-card__title .reversed-link__text',
      cardPrice: '.f-price',
      cardVariants: '[data-quick-add-variants]',
      cardLink: '[data-variant-link]',
      chip: '.quick-add-drawer__size',
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.onSizeClick.bind(this), {
      signal: this.abortController.signal,
    });

    // ProductForm despacha product-ajax:added recién cuando termina el fetch:
    // cerramos ahí para que el cart drawer tome la pantalla con el item ya
    // agregado. Un error de carrito no despacha el evento, así que el drawer
    // queda abierto, que es lo que queremos.
    document.addEventListener('product-ajax:added', () => this.hide(), {
      signal: this.abortController.signal,
    });
  }

  onSizeClick(event) {
    // El chequeo de disabled es defensa en profundidad: un <button disabled> ya
    // no emite click y el CSS le saca pointer-events, pero no queremos que el
    // talle agotado dependa solo de esas dos coincidencias.
    const chip = event.target.closest(this.selectors.chip);
    if (!chip || chip.disabled) return;

    this.addVariant(chip.dataset.variantId);
  }

  /**
   * Setea la variante en el form del drawer y lo envía. A partir de ahí se
   * ocupa ProductForm: fetch, errores, pubsub y apertura del cart drawer.
   * @param {string|number} variantId
   */
  addVariant(variantId) {
    const form = this.querySelector(this.selectors.form);
    form.querySelector(this.selectors.variantInput).value = variantId;
    form.requestSubmit();
  }

  /**
   * Copia título, precio y talles de la card al drawer.
   * @param {HTMLElement} card
   * @returns {boolean} false cuando no hay talles que mostrar, para que el
   * llamador caiga a la PDP.
   */
  populate(card) {
    const sizes = this.getSizes(card);
    if (!sizes.length) return false;

    this.querySelector(this.selectors.sizes).replaceChildren(
      ...sizes.map((variant) => this.buildChip(variant))
    );

    const title = card.querySelector(this.selectors.cardTitle);
    this.querySelector(this.selectors.title).textContent = title ? title.textContent.trim() : '';

    const price = card.querySelector(this.selectors.cardPrice);
    this.querySelector(this.selectors.price).innerHTML = price ? price.outerHTML : '';

    return true;
  }

  /**
   * Variantes del color activo, en el orden en que las devuelve Shopify.
   * @param {HTMLElement} card
   * @returns {Array<{i: number, c: string, s: string, a: boolean}>}
   */
  getSizes(card) {
    const script = card.querySelector(this.selectors.cardVariants);
    if (!script) return [];

    let variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }

    const color = this.getActiveColor(card, variants);
    if (!color) return variants;

    return variants.filter((variant) => variant.c === color);
  }

  /**
   * Color que la card está mostrando, leído del href que
   * ColorSwatch.updateLinks() mantiene al día.
   * @param {HTMLElement} card
   * @param {Array<{i: number, c: string}>} variants
   * @returns {string} vacío cuando el producto no tiene opción de color
   */
  getActiveColor(card, variants) {
    if (!variants.some((variant) => variant.c !== '')) return '';

    const link = card.querySelector(this.selectors.cardLink);
    const id = link ? Number(new URL(link.href, window.location.origin).searchParams.get('variant')) : NaN;
    const current = variants.find((variant) => variant.i === id);

    // Sin ?variant= en el href caemos al primer color, que es el que la card
    // muestra por defecto.
    return current ? current.c : variants[0].c;
  }

  /**
   * @param {{i: number, s: string, a: boolean}} variant
   * @returns {HTMLButtonElement}
   */
  buildChip(variant) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'quick-add-drawer__size';
    chip.dataset.variantId = variant.i;
    chip.textContent = variant.s;

    if (!variant.a) {
      chip.classList.add('quick-add-drawer__size--unavailable');
      chip.disabled = true;
    }

    return chip;
  }
}
customElements.define('quick-add-drawer', QuickAddDrawer);

// Delegado en document: los filtros y la paginación AJAX re-renderizan la
// grilla, y ModalComponent bindea sus [aria-controls] una sola vez.
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quick-add]');
  if (!button) return;

  event.preventDefault();

  const drawer = document.getElementById('QuickAddDrawer');
  if (!drawer || typeof drawer.addVariant !== 'function') return;

  // Producto de una sola variante: no hay talle que elegir.
  const variantId = button.getAttribute('data-quick-add-variant');
  if (variantId) {
    drawer.addVariant(variantId);
    return;
  }

  const card = button.closest('.product-card');
  const url = button.getAttribute('data-quick-add-url');

  if (card && drawer.populate(card)) {
    drawer.show(button);
  } else if (url) {
    window.location.href = url;
  }
});
